import { useMemo } from "react";
import { Input } from "../../sharedComponents/inputs/Input";
import { useForm } from "react-hook-form";
import {
  createUserFromPerson,
  updateUser,
} from "../../supportFunctions/graphql/PersonQueries";
import {
  attemptShowErrorsInSnackbar,
  showSnackbarMessage,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import { PersonUserForm } from "./Person";
import UsersService from "@velocity-ehs/user-utils-module";
import { useTranslation } from "react-i18next";
import {
  UnsavedChangesPrompt,
  confirmDialog,
} from "@velocity-ehs/component-library";
import { useHistory } from "react-router-dom";
import GatewayService from "../../../services/GatewayService";
import classes from "./UserForm.module.scss";
import useSendResetPasswordEmailMutation from "../../../hooks/useSendResetPasswordEmailMutation";
import { emailRegExp } from "../../supportFunctions/ValidationHelpers";

type UserFormProps = {
  refetch: () => void;
  user?: PersonUserForm;
  personId?: string;
  personEmail?: string;
  readOnly: boolean;
  testId?: string;
};

const UserForm = ({
  user,
  personId,
  personEmail,
  readOnly,
  refetch,
  testId,
}: UserFormProps) => {
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();
  const history = useHistory();

  const { mutations } = useSendResetPasswordEmailMutation();

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    getValues,
  } = useForm<{
    active: boolean;
    username: string;
    email: string;
  }>({
    mode: "onBlur",
    defaultValues: useMemo(
      () => ({
        email: user?.userDemographic.email || personEmail,
        username: user?.displayedLoginName,
        active: user?.active,
      }),
      [user, personEmail]
    ),
  });

  const onSubmit = async (values: {
    username: string;
    email: string;
    active: boolean;
  }) => {
    try {
      await GatewayService.updatePerson({
        email: values.email,
        id: personId,
      });
    } catch (error) {
      throw attemptShowErrorsInSnackbar(showSnackbar, error);
    }

    if (user) {
      if (user.displayedLoginName) {
        return GatewayService.api
          .post(
            "",
            updateUser({
              active: values.active,
              id: user?.id,
              username: values.username,
            })
          )
          .then(() => {
            showSnackbarMessage(
              showSnackbar,
              SnackbarType.Success,
              t("person_user_updated")
            );

            reset(getValues());
          })
          .catch((error: any) => {
            attemptShowErrorsInSnackbar(showSnackbar, error);
          });
      }
    }

    if (personId) {
      return GatewayService.api
        .post(
          "",
          createUserFromPerson({
            customerId: UsersService.customer?.id,
            username: values.username,
            email: values.email,
            personId,
          })
        )
        .then(() => {
          refetch();

          showSnackbarMessage(
            showSnackbar,
            SnackbarType.Success,
            t("person_user_created")
          );
        })
        .catch((error: any) => {
          attemptShowErrorsInSnackbar(showSnackbar, error);
        });
    }
  };

  const handleSendResetEmail = async () => {
    mutations.mutate(user);
  };

  const canEditUsers = UsersService.permissions.canEditUsers;

  return (
    <>
      <form
        id="personForm"
        onSubmit={handleSubmit(onSubmit)}
        data-testid={testId}
      >
        <Input
          readOnly={readOnly}
          control={control}
          name="username"
          label={t("universal_Username")}
          rules={{
            required: t("customer_Form_Admin_User_Username_Required"),
            pattern: {
              value: /^\s*[^+\\;,]+\s*$/i,
              message: t("message_invalid_username"),
            },
            maxLength: {
              value: 64,
              message: t(
                "customer_Form_Admin_User_Username_Max_Length_Validation"
              ),
            },
          }}
          errorMessage={errors ? errors.username?.message : undefined}
        />
        <Input
          control={control}
          name="email"
          label={t("universal_Email")}
          readOnly={!canEditUsers}
          rules={{
            required: t("customer_Form_Admin_User_Email_Required"),
            maxLength: {
              value: 100,
              message: t("max_length_100_char_form_validation"),
            },
            pattern: {
              value: emailRegExp,
              message: t("message_invalid_email_address"),
            },
          }}
          errorMessage={errors ? errors.email?.message : undefined}
        />
      </form>
      {canEditUsers && (
        <button
          className={classes.resetEmailButton}
          disabled={getValues("email") === null}
          type="button"
          onClick={() =>
            confirmDialog({
              message: t("reset_password_email_message"),
              header: t("reset_password_email_header"),
              accept: handleSendResetEmail,
              reject: () => {},
              acceptLabel: t("universal_yes"),
              rejectLabel: t("universal_no"),
            })
          }
        >
          {t("reset_password_send_email_message")}
        </button>
      )}
      <UnsavedChangesPrompt isDirty={isDirty} history={history} />
    </>
  );
};

export { UserForm };
